import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';

// Phase 123 - non-English locale bundles (~200 KiB) are lazy chunks
// loaded only when the user actually uses that language.
const localeLoaders = {
  hi: () => import('./locales/hi.json'),
  bn: () => import('./locales/bn.json'),
  ta: () => import('./locales/ta.json'),
  te: () => import('./locales/te.json'),
  mr: () => import('./locales/mr.json'),
  gu: () => import('./locales/gu.json'),
  kn: () => import('./locales/kn.json'),
  ml: () => import('./locales/ml.json'),
  pa: () => import('./locales/pa.json'),
  ur: () => import('./locales/ur.json'),
  es: () => import('./locales/es.json'),
  fr: () => import('./locales/fr.json'),
  zh: () => import('./locales/zh.json'),
  de: () => import('./locales/de.json'),
  ja: () => import('./locales/ja.json'),
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
    },
    fallbackLng: 'en',
    debug: false,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

export async function ensureLocaleBundle(lng) {
  const base = String(lng || 'en').split('-')[0];
  if (base === 'en' || i18n.hasResourceBundle(base, 'translation')) return;
  const loader = localeLoaders[base];
  if (!loader) return;
  try {
    const mod = await loader();
    i18n.addResourceBundle(base, 'translation', mod.default || mod, true, true);
  } catch (_e) {
    // Chunk failed to load - English fallback strings keep rendering.
  }
}

// Every language switch first ensures the bundle is loaded.
const _rawChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = async (lng, ...rest) => {
  if (lng) await ensureLocaleBundle(lng);
  return _rawChangeLanguage(lng, ...rest);
};

// Hydrate the detector-picked locale on first load (non-blocking;
// English shows for a beat, then the saved language snaps in).
if (i18n.language && !String(i18n.language).startsWith('en')) {
  ensureLocaleBundle(i18n.language).then(() => _rawChangeLanguage(i18n.language));
}

// Use Noto Sans [Script] as the primary font for every script.
// Noto fonts are designed to fully support each script with no glyph overlap.
// Fallbacks are provided to a complementary playful font, then sans-serif.
export const languageFontMap = {
  en: {
    heading: '"Fredoka", "Nunito", sans-serif',
    body: '"Nunito", sans-serif',
    lineHeight: 1.6
  },
  hi: {
    heading: '"Noto Sans Devanagari", "Baloo 2", sans-serif',
    body: '"Noto Sans Devanagari", sans-serif',
    lineHeight: 1.8
  },
  mr: {
    heading: '"Noto Sans Devanagari", "Baloo 2", sans-serif',
    body: '"Noto Sans Devanagari", sans-serif',
    lineHeight: 1.8
  },
  bn: {
    heading: '"Noto Sans Bengali", "Baloo Da 2", sans-serif',
    body: '"Noto Sans Bengali", sans-serif',
    lineHeight: 1.9
  },
  ta: {
    heading: '"Noto Sans Tamil", "Baloo Thambi 2", sans-serif',
    body: '"Noto Sans Tamil", sans-serif',
    lineHeight: 1.9
  },
  te: {
    heading: '"Noto Sans Telugu", "Baloo Tammudu 2", sans-serif',
    body: '"Noto Sans Telugu", sans-serif',
    lineHeight: 1.9
  },
  gu: {
    heading: '"Noto Sans Gujarati", "Baloo Bhai 2", sans-serif',
    body: '"Noto Sans Gujarati", sans-serif',
    lineHeight: 1.8
  },
  kn: {
    heading: '"Noto Sans Kannada", "Baloo Tamma 2", sans-serif',
    body: '"Noto Sans Kannada", sans-serif',
    lineHeight: 1.9
  },
  ml: {
    heading: '"Noto Sans Malayalam", "Baloo Chettan 2", sans-serif',
    body: '"Noto Sans Malayalam", sans-serif',
    lineHeight: 1.9
  },
  pa: {
    heading: '"Noto Sans Gurmukhi", "Baloo Paaji 2", sans-serif',
    body: '"Noto Sans Gurmukhi", sans-serif',
    lineHeight: 1.8
  },
  ur: {
    heading: '"Noto Nastaliq Urdu", serif',
    body: '"Noto Nastaliq Urdu", serif',
    lineHeight: 2.2
  },
  es: {
    heading: '"Fredoka", "Nunito", sans-serif',
    body: '"Nunito", sans-serif',
    lineHeight: 1.6
  },
  fr: {
    heading: '"Fredoka", "Nunito", sans-serif',
    body: '"Nunito", sans-serif',
    lineHeight: 1.6
  },
  zh: {
    heading: '"Noto Sans SC", "Noto Sans", sans-serif',
    body: '"Noto Sans SC", sans-serif',
    lineHeight: 1.8
  },
  de: {
    heading: '"Fredoka", "Nunito", sans-serif',
    body: '"Nunito", sans-serif',
    lineHeight: 1.6
  },
  ja: {
    heading: '"Noto Sans JP", "Noto Sans", sans-serif',
    body: '"Noto Sans JP", sans-serif',
    lineHeight: 1.8
  },
};

export const supportedLanguages = [
  // Global. `englishName` is the English label used by the
  // CreateStorybook dropdown and the Gemini generation prompt; `name`
  // stays as the native-script display label for the dashboard
  // language switcher.
  { code: 'en', label: 'EN', name: 'English',  englishName: 'English',  group: 'Global' },
  { code: 'es', label: 'ES', name: 'Español',  englishName: 'Spanish',  group: 'Global' },
  { code: 'fr', label: 'FR', name: 'Français', englishName: 'French',   group: 'Global' },
  { code: 'zh', label: '中', name: '中文',      englishName: 'Mandarin', group: 'Global' },
  { code: 'de', label: 'DE', name: 'Deutsch',  englishName: 'German',   group: 'Global' },
  { code: 'ja', label: '日', name: '日本語',     englishName: 'Japanese', group: 'Global' },
  // Indian regional
  { code: 'hi', label: 'हि', name: 'हिन्दी',     englishName: 'Hindi',     group: 'India' },
  { code: 'bn', label: 'বা', name: 'বাংলা',     englishName: 'Bengali',   group: 'India' },
  { code: 'ta', label: 'த', name: 'தமிழ்',     englishName: 'Tamil',     group: 'India' },
  { code: 'te', label: 'తె', name: 'తెలుగు',    englishName: 'Telugu',    group: 'India' },
  { code: 'mr', label: 'म', name: 'मराठी',     englishName: 'Marathi',   group: 'India' },
  { code: 'gu', label: 'ગુ', name: 'ગુજરાતી',   englishName: 'Gujarati',  group: 'India' },
  { code: 'kn', label: 'ಕ', name: 'ಕನ್ನಡ',     englishName: 'Kannada',   group: 'India' },
  { code: 'ml', label: 'മ', name: 'മലയാളം',   englishName: 'Malayalam', group: 'India' },
  { code: 'pa', label: 'ਪੰ', name: 'ਪੰਜਾਬੀ',     englishName: 'Punjabi',   group: 'India' },
  { code: 'ur', label: 'اُ', name: 'اردو',     englishName: 'Urdu',      group: 'India' },
];

// Phase 119.14 - lazy-loaded non-Latin Google Fonts (Hindi, Tamil, JP, ZH, …)
// fire ONLY when the user actually selects that locale.
import { loadFontForLocale } from './lib/loadFontForLocale';

export function applyLanguageFonts(lang) {
  const fonts = languageFontMap[lang] || languageFontMap.en;
  const root = document.documentElement;
  root.style.setProperty('--font-heading', fonts.heading);
  root.style.setProperty('--font-body', fonts.body);
  root.style.setProperty('--line-height-base', String(fonts.lineHeight || 1.6));
  root.setAttribute('dir', lang === 'ur' ? 'rtl' : 'ltr');
  root.setAttribute('lang', lang);
  // Inject the matching Google-Fonts <link> only if this locale needs
  // a non-Latin script. No-op for en/es/fr/de/it/pt.
  loadFontForLocale(lang);
}

i18n.on('languageChanged', (lng) => {
  applyLanguageFonts(lng);
});

applyLanguageFonts(i18n.language);

export default i18n;
